/**
 * RecurringOrdersPanel - Display and manage recurring orders for a customer
 *
 * Shows recurring orders grouped by branch with actions to:
 * - Pause (temporarily stop) an order until a specific date
 * - Resume a paused order
 * - Cancel (permanently stop) an order
 * - Edit (delegated to Step 8)
 *
 * US-RECURRING-ORDERS Step 7: Customer Page - Recurring Orders Tab
 */

import { useState, useEffect, useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import { RefreshCcw, Pause, Play, X, Pencil, Store, SkipForward } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { DatePicker } from '@/components/ui/date-picker'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import {
  fetchRecurringOrders,
  fetchPendingInstances,
  pauseRecurringOrder,
  resumeRecurringOrder,
  cancelRecurringOrder
} from '../services/recurringOrderService'
import EditRecurringOrderDialog from './EditRecurringOrderDialog'

// Day names for display (ISO weekday: 1=Mon, 7=Sun)
const DAY_NAMES = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday'
}

const SHORT_DAY_NAMES = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun'
}

/**
 * Format days of week into readable string
 * e.g., [2] -> "Every Tuesday"
 * e.g., [2, 4] -> "Every Tuesday and Thursday"
 * e.g., [1, 3, 5] -> "Every Monday, Wednesday, and Friday"
 */
function formatDaysOfWeek(daysOfWeek) {
  if (!daysOfWeek || daysOfWeek.length === 0) return 'No days selected'

  const sortedDays = [...daysOfWeek].sort((a, b) => a - b)
  const dayNames = sortedDays.map(d => DAY_NAMES[d])

  if (dayNames.length === 1) {
    return `Every ${dayNames[0]}`
  } else if (dayNames.length === 2) {
    return `Every ${dayNames[0]} and ${dayNames[1]}`
  } else {
    const lastDay = dayNames.pop()
    return `Every ${dayNames.join(', ')}, and ${lastDay}`
  }
}

/**
 * Format items list for display
 * e.g., "Grey Oyster: 3kg, Lions Mane: 1kg"
 */
function formatItemsList(items) {
  if (!items || items.length === 0) return 'No items'

  return items.map(item => {
    const productName = item.product?.strain || 'Unknown'
    const quantity = item.quantity
    return `${productName}: ${quantity}kg`
  }).join(', ')
}

/**
 * RecurringOrderCard - Individual order card with actions
 */
function RecurringOrderCard({
  order,
  nextInstanceDate,
  onPause,
  onResume,
  onEdit,
  onCancel,
  disabled
}) {
  const isPaused = order.paused_until && new Date(order.paused_until) > new Date()
  const createdDate = order.created_at ? format(parseISO(order.created_at), 'd MMM yyyy') : 'Unknown'

  return (
    <div className="border rounded-lg p-4 mb-3 bg-white hover:shadow-sm transition-shadow">
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900">
              {formatDaysOfWeek(order.days_of_week)}
            </h4>
            <Badge variant={isPaused ? 'warning' : 'success'} className="text-xs">
              {isPaused
                ? `Paused until ${format(parseISO(order.paused_until), 'd MMM')}`
                : 'Active'
              }
            </Badge>
          </div>

          <p className="text-sm text-gray-600 mt-1 truncate">
            {formatItemsList(order.items)}
          </p>

          <p className="text-xs text-gray-500 mt-2">
            Since {createdDate}
            {nextInstanceDate && !isPaused && (
              <> &bull; Next: {format(parseISO(nextInstanceDate), 'd MMM yyyy')}</>
            )}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-3 flex-wrap">
        {isPaused ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onResume(order)}
            disabled={disabled}
            className="gap-1"
          >
            <Play className="h-3 w-3" />
            Resume
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPause(order)}
            disabled={disabled}
            className="gap-1"
          >
            <Pause className="h-3 w-3" />
            Pause
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(order)}
          disabled={disabled}
          className="gap-1"
        >
          <Pencil className="h-3 w-3" />
          Edit
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onCancel(order)}
          disabled={disabled}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
        >
          <X className="h-3 w-3" />
          Cancel
        </Button>
      </div>
    </div>
  )
}

/**
 * PauseDialog - Dialog for selecting pause duration
 */
function PauseDialog({ order, open, onClose, onConfirm, loading }) {
  const [pauseUntil, setPauseUntil] = useState(null)

  // Reset date when dialog opens
  useEffect(() => {
    if (open) {
      // Default to 2 weeks from now
      const twoWeeksFromNow = new Date()
      twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14)
      setPauseUntil(twoWeeksFromNow)
    }
  }, [open])

  const handleConfirm = () => {
    if (pauseUntil) {
      onConfirm(pauseUntil)
    }
  }

  // Minimum date is tomorrow
  const minDate = new Date()
  minDate.setDate(minDate.getDate() + 1)

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Pause Recurring Order</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Pause deliveries for &quot;{formatDaysOfWeek(order?.days_of_week)}&quot; until:
          </p>

          <DatePicker
            date={pauseUntil}
            onDateChange={setPauseUntil}
            placeholder="Select resume date"
            disabled={loading}
          />

          <p className="text-xs text-gray-500">
            Scheduled deliveries before this date will be skipped. The order will automatically resume on this date.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !pauseUntil}
          >
            {loading ? 'Pausing...' : 'Pause Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * CancelDialog - Confirmation dialog for cancelling an order
 */
function CancelDialog({ order, open, onClose, onConfirm, loading }) {
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel Recurring Order?</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          This will permanently stop all future deliveries for &quot;{formatDaysOfWeek(order?.days_of_week)}&quot;.
          This action cannot be undone.
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose} disabled={loading}>
            Keep Order
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            variant="destructive"
            disabled={loading}
          >
            {loading ? 'Cancelling...' : 'Cancel Order'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * RecurringOrdersPanel - Main component
 */
export default function RecurringOrdersPanel({ customerId }) {
  const [recurringOrders, setRecurringOrders] = useState([])
  const [pendingInstances, setPendingInstances] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Dialog states
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)

  // Load recurring orders and pending instances
  const loadData = async () => {
    if (!customerId) return

    setLoading(true)
    setError(null)

    try {
      // Fetch recurring orders and pending instances in parallel
      const [orders, instances] = await Promise.all([
        fetchRecurringOrders({ customerId, activeOnly: true }),
        fetchPendingInstances({ customerId, limit: 100 })
      ])

      setRecurringOrders(orders)
      setPendingInstances(instances)
    } catch (err) {
      console.error('[RecurringOrdersPanel] Error loading data:', err)
      setError('Failed to load recurring orders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [customerId])

  // Group orders by branch
  const ordersByBranch = useMemo(() => {
    const grouped = {}

    recurringOrders.forEach(order => {
      const branchKey = order.branch_id || 'no-branch'
      const branchName = order.branch?.name || 'Customer-level'

      if (!grouped[branchKey]) {
        grouped[branchKey] = {
          branchId: order.branch_id,
          branchName,
          orders: []
        }
      }

      grouped[branchKey].orders.push(order)
    })

    return Object.values(grouped)
  }, [recurringOrders])

  // Get next instance date for an order
  const getNextInstanceDate = (orderId) => {
    const instance = pendingInstances.find(
      i => i.recurring_order_id === orderId
    )
    return instance?.scheduled_date || null
  }

  // Action handlers
  const handlePauseClick = (order) => {
    setSelectedOrder(order)
    setPauseDialogOpen(true)
  }

  const handlePauseConfirm = async (pauseUntilDate) => {
    if (!selectedOrder) return

    setActionLoading(true)
    try {
      // Format date as YYYY-MM-DD
      const dateStr = format(pauseUntilDate, 'yyyy-MM-dd')
      await pauseRecurringOrder(selectedOrder.id, dateStr)

      toast.success('Order paused', {
        description: `Deliveries paused until ${format(pauseUntilDate, 'd MMM yyyy')}`
      })

      setPauseDialogOpen(false)
      setSelectedOrder(null)
      await loadData() // Refresh data
    } catch (err) {
      console.error('[RecurringOrdersPanel] Error pausing order:', err)
      toast.error('Failed to pause order', {
        description: err.message || 'Please try again'
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleResumeClick = async (order) => {
    setActionLoading(true)
    try {
      await resumeRecurringOrder(order.id)

      toast.success('Order resumed', {
        description: 'Deliveries will continue as scheduled'
      })

      await loadData() // Refresh data
    } catch (err) {
      console.error('[RecurringOrdersPanel] Error resuming order:', err)
      toast.error('Failed to resume order', {
        description: err.message || 'Please try again'
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleEditClick = (order) => {
    setSelectedOrder(order)
    setEditDialogOpen(true)
  }

  const handleEditSave = async () => {
    setEditDialogOpen(false)
    setSelectedOrder(null)
    toast.success('Recurring order updated', {
      description: 'Future deliveries will reflect your changes'
    })
    await loadData() // Refresh the list
  }

  const handleCancelClick = (order) => {
    setSelectedOrder(order)
    setCancelDialogOpen(true)
  }

  const handleCancelConfirm = async () => {
    if (!selectedOrder) return

    setActionLoading(true)
    try {
      await cancelRecurringOrder(selectedOrder.id)

      toast.success('Order cancelled', {
        description: 'All future deliveries have been stopped'
      })

      setCancelDialogOpen(false)
      setSelectedOrder(null)
      await loadData() // Refresh data
    } catch (err) {
      console.error('[RecurringOrdersPanel] Error cancelling order:', err)
      toast.error('Failed to cancel order', {
        description: err.message || 'Please try again'
      })
    } finally {
      setActionLoading(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg">Recurring Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-green-600 rounded-full animate-spin" />
            <span className="ml-3 text-gray-500">Loading recurring orders...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg">Recurring Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <Button variant="outline" onClick={loadData} className="gap-2">
              <RefreshCcw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Empty state
  if (recurringOrders.length === 0) {
    return (
      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg">Recurring Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <RefreshCcw className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h4 className="text-gray-900 font-medium mb-1">No recurring orders</h4>
            <p className="text-gray-500 text-sm">
              Create a recurring order from the Sales page by checking
              &quot;Make this a recurring order&quot; when creating a sale.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Main content - orders grouped by branch
  return (
    <>
      <Card className="border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Recurring Orders</CardTitle>
            <Badge variant="secondary" className="text-xs">
              {recurringOrders.length} order{recurringOrders.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {ordersByBranch.map((group) => (
            <div key={group.branchId || 'no-branch'}>
              {/* Branch header */}
              <div className="flex items-center gap-2 mb-3">
                <Store className="h-4 w-4 text-gray-500" />
                <h4 className="font-medium text-gray-700">{group.branchName}</h4>
              </div>

              {/* Orders for this branch */}
              {group.orders.map((order) => (
                <RecurringOrderCard
                  key={order.id}
                  order={order}
                  nextInstanceDate={getNextInstanceDate(order.id)}
                  onPause={handlePauseClick}
                  onResume={handleResumeClick}
                  onEdit={handleEditClick}
                  onCancel={handleCancelClick}
                  disabled={actionLoading}
                />
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Pause Dialog */}
      <PauseDialog
        order={selectedOrder}
        open={pauseDialogOpen}
        onClose={() => {
          setPauseDialogOpen(false)
          setSelectedOrder(null)
        }}
        onConfirm={handlePauseConfirm}
        loading={actionLoading}
      />

      {/* Cancel Confirmation Dialog */}
      <CancelDialog
        order={selectedOrder}
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false)
          setSelectedOrder(null)
        }}
        onConfirm={handleCancelConfirm}
        loading={actionLoading}
      />

      {/* Edit Recurring Order Dialog */}
      <EditRecurringOrderDialog
        order={selectedOrder}
        open={editDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditDialogOpen(false)
            setSelectedOrder(null)
          }
        }}
        onSave={handleEditSave}
      />
    </>
  )
}
